// frontend/admin-portal/src/pages/OrderPrint.jsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  PageHeader,
  Button,
  Card,
  Spinner,
  Alert,
  InvoiceTemplate,
} from "@fiberhome/shared-ui";
import { orderService } from "../services/orderService";
import { resolveImageUrl } from "../services/fileService";
import { useStoreIdentity } from "../hooks/useStoreIdentity";

/* ------------------------------------------------------------------ */
/*  Local Error Boundary — prevents redirect to / on render errors    */
/* ------------------------------------------------------------------ */
class PrintErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[OrderPrint] render error:", error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <>
          <PageHeader
            title="Invoice Preview"
            breadcrumbs={["Home", "Sales", "Print"]}
          />
          <Alert variant="danger" title="Could not render the invoice">
            <div style={{ fontSize: 13 }}>
              {this.state.error?.message || "Unknown rendering error"}
            </div>
            <pre
              style={{ fontSize: 11, marginTop: 10, whiteSpace: "pre-wrap" }}
            >
              {this.state.error?.stack}
            </pre>
            <div className="mt-3">
              <Button variant="primary" onClick={this.props.onBack}>
                ← Back to Order
              </Button>
            </div>
          </Alert>
        </>
      );
    }
    return this.props.children;
  }
}

/* ------------------------------------------------------------------ */
/*  Page                                                              */
/* ------------------------------------------------------------------ */
export default function OrderPrint() {
  const { id } = useParams();
  const navigate = useNavigate();
  const store = useStoreIdentity();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lang, setLang] = useState("en");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError("");
    orderService
      .get(id)
      .then((o) => {
        if (!alive) return;
        setOrder(o);
      })
      .catch((err) => {
        if (!alive) return;
        setError(
          err.response?.data?.message || err.message || "Failed to load",
        );
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [id]);

  if (loading) {
    return (
      <>
        <PageHeader title="Invoice Preview" />
        <Card>
          <div className="fh-settings-loading">
            <Spinner size="lg" />
          </div>
        </Card>
      </>
    );
  }

  if (error || !order) {
    return (
      <>
        <PageHeader title="Not found" />
        <Alert variant="danger">{error || "Order not found"}</Alert>
        <Button
          variant="outline"
          className="mt-3"
          onClick={() => navigate("/orders")}
        >
          ← Back
        </Button>
      </>
    );
  }

  const kind = order.kind === "offer" ? "offer" : "invoice";
  const logoUrl = store.logo ? resolveImageUrl(store.logo) : "";

  return (
    <PrintErrorBoundary onBack={() => navigate(`/orders/${order.id}`)}>
      <div className="no-print">
        <PageHeader
          title="Invoice Preview"
          subtitle={`${order.code} — print or save as PDF`}
          breadcrumbs={["Home", "Sales", order.code, "Print"]}
          actions={
            <div className="d-flex gap-2 flex-wrap">
              <select
                className="fh-input fh-input--compact"
                value={lang}
                onChange={(e) => setLang(e.target.value)}
              >
                <option value="en">English</option>
                <option value="ku">Kurdish</option>
              </select>
              <Button
                variant="outline"
                onClick={() => navigate(`/orders/${order.id}`)}
              >
                Back to Order
              </Button>
              <Button variant="primary" onClick={() => window.print()}>
                🖨 Print / Save as PDF
              </Button>
            </div>
          }
        />
      </div>

      <InvoiceTemplate
        kind={kind}
        order={order}
        store={store}
        lang={lang}
        logoUrl={logoUrl}
      />
    </PrintErrorBoundary>
  );
}
